/**
 * Event Management Service
 *
 * Unified service for event management that combines:
 * - Rule CRUD operations
 * - Event browsing: time-range fetch + facet filtering over CloudWatch Logs
 *   (events.GetEvents), with optional auto-refresh polling (useEventPolling)
 * - Event traces, templates, and the action log
 *
 * This service follows the factory + hook pattern used throughout the app.
 */

import { useMemo, useRef, useCallback } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';
import type {
  EventRule,
  EventRuleListItem,
  RuleCreate,
  ItsmChannel,
  AutomationChannelRequest,
  ResolveSignal,
  PatternTestRequest,
  PatternTestResponse,
  LiveEvent,
  EventsResponse,
  ActionLogEntry,
  ActionLogResponse,
  PreviewRuleOutputRequest,
  PreviewRuleOutputResponse,
  SuggestFieldMappingsRequest,
  SuggestFieldMappingsResponse,
  GetRuleSampleEventRequest,
  RuleSampleEvent,
  EventDataset,
  EventDatasetRecord,
  EventDatasetRetentionCeiling,
  EventDatasetStatus,
  EventDatasetMisfit,
  EventDatasetPreview,
  EventDatasetDeleted,
  CreateCustomerEventDatasetTemplateRequest,
  EventDatasetTemplate,
  EventDatasetTemplateDeleted,
  QueryEventDatasetRequest,
  EventDatasetRows,
} from '../../../types/backend';
import type {
  TransformTestRequest,
  TransformTestResponse,
  EventFilters,
  EventRuleExtended,
  EventRuleListItemExtended,
  RuleCreateExtended,
  TriggerType,
  EventTrace,
  RuleTemplate,
  RuleTemplateListItem,
  TemplateOwnerType,
  SchedulePreset,
} from '../../../types/frontend';
import { buildPresetCron, getPresetFromExpression as parsePreset } from '../utils/scheduleUtils';

/**
 * Convert backend rule to extended rule with trigger type.
 * Backend uses the nested per-channel C-02 shape natively.
 */
const toExtendedRule = (rule: EventRule): EventRuleExtended => {
  const hasSchedule = !!rule.schedule_expression;

  return {
    rule_id: rule.rule_id,
    rule_type: rule.rule_type,
    name: rule.name,
    description: rule.description,
    trigger_type: hasSchedule ? 'schedule' : 'event',
    event_pattern: rule.event_pattern,
    schedule_expression: rule.schedule_expression,
    schedule_preset: rule.schedule_expression
      ? getPresetFromExpression(rule.schedule_expression)
      : undefined,
    status: rule.status,
    created_at: rule.created_at,
    desired_status: rule.desired_status,
    deployments: rule.deployments,
    event_examples: rule.event_examples,
    opsitem_strategy: rule.opsitem_strategy,
    dedup_field_paths: rule.dedup_field_paths ?? [],
    enrichment_prompt_id: rule.enrichment_prompt_id ?? null,
    summarize: rule.summarize ?? true,
    itsm: rule.itsm ?? null,
    notification_targets: rule.notification_targets ?? [],
    automation: rule.automation ?? null,
    resolve_signal: rule.resolve_signal ?? null,
  };
};

/**
 * Convert backend list item to extended list item.
 * Backend now uses has_itsm/has_automation instead of target_types.
 */
const toExtendedListItem = (item: EventRuleListItem): EventRuleListItemExtended => {
  return {
    ...item,
    trigger_type: (item.trigger_type || 'event') as TriggerType,
    has_itsm: item.has_itsm ?? false,
    has_automation: item.has_automation ?? false,
  };
};

/**
 * Convert extended create request to backend create request.
 * Backend accepts the nested per-channel C-02 shape directly.
 */
const toBackendCreate = (request: RuleCreateExtended): RuleCreate => {
  let scheduleExpression: string | undefined;
  if (request.trigger_type === 'schedule') {
    if (request.schedule_expression) {
      scheduleExpression = request.schedule_expression;
    } else if (request.schedule_preset) {
      scheduleExpression = getScheduleExpression(request.schedule_preset);
    }
  }

  return {
    customer_id: request.customer_id,
    rule_type: request.rule_type,
    name: request.name,
    description: request.description || '',
    trigger_type: request.trigger_type,
    event_pattern: request.trigger_type === 'event' ? (request.event_pattern || {}) : undefined,
    schedule_expression: scheduleExpression,
    enrichment_prompt_id: request.enrichment_prompt_id ?? undefined,
    summarize: request.summarize ?? true,
    itsm: request.itsm ?? undefined,
    notification_targets: request.notification_targets ?? [],
    automation: request.automation ?? undefined,
    resolve_signal: request.resolve_signal ?? undefined,
    enabled: request.enabled ?? true,
    event_examples: request.event_examples || [],
    opsitem_strategy: request.opsitem_strategy,
    dedup_field_paths: request.dedup_field_paths ?? [],
  };
};

/**
 * Creates an event management service instance
 */
export const createEventManagementService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  return {
    getEventRules: async (customerId: string): Promise<EventRuleListItemExtended[]> => {
      const result = await rpcCall(client, 'events.ListRules', { customer_id: customerId });
      return result.map(toExtendedListItem);
    },

    getEventRule: async (customerId: string, ruleId: string): Promise<EventRuleExtended> => {
      const result = await rpcCall(client, 'events.GetRule', { customer_id: customerId, rule_id: ruleId });
      return toExtendedRule(result);
    },

    createEventRule: async (
      customerId: string,
      request: RuleCreateExtended
    ): Promise<EventRuleExtended> => {
      const backendRequest = toBackendCreate(request);
      const result = await rpcCall(client, 'events.CreateRule', { ...backendRequest, customer_id: customerId });
      return toExtendedRule(result);
    },

    updateEventRule: async (
      customerId: string,
      ruleId: string,
      updates: Partial<RuleCreateExtended>
    ): Promise<EventRuleExtended> => {
      let scheduleExpression: string | undefined;
      if (updates.trigger_type === 'schedule') {
        if (updates.schedule_expression) {
          scheduleExpression = updates.schedule_expression;
        } else if (updates.schedule_preset) {
          scheduleExpression = getScheduleExpression(updates.schedule_preset);
        }
      }

      const backendUpdates: Partial<Omit<RuleCreate, 'itsm' | 'automation' | 'enrichment_prompt_id' | 'resolve_signal'>> & {
        itsm?: ItsmChannel | null;
        automation?: AutomationChannelRequest | null;
        enrichment_prompt_id?: string | null;
        resolve_signal?: ResolveSignal | null;
      } = {
        description: updates.description,
        trigger_type: updates.trigger_type,
        event_pattern: updates.trigger_type === 'event' ? updates.event_pattern : undefined,
        schedule_expression: scheduleExpression,
        summarize: updates.summarize,
        // null (not undefined) clears a field: undefined is stripped by JSON
        // serialization, which the backend reads as "unchanged" and keeps the old value.
        enrichment_prompt_id: updates.enrichment_prompt_id ?? null,
        itsm: updates.itsm ?? null,
        notification_targets: updates.notification_targets ?? [],
        automation: updates.automation ?? null,
        // null (not undefined) clears the recovery signal when auto-resolve is
        // toggled off; undefined would be stripped and read as "unchanged".
        resolve_signal: updates.resolve_signal ?? null,
        enabled: updates.enabled,
        event_examples: updates.event_examples,
        opsitem_strategy: updates.opsitem_strategy,
        // null (not undefined) is stripped by JSON serialization; send [] so the
        // backend actually clears paths when switching away from fingerprint.
        dedup_field_paths: updates.dedup_field_paths ?? [],
      };

      // Escape-hatch: backendUpdates sends `null` to CLEAR fields (see above), but
      // the generated UpdateRuleRequest types nullable fields as `?: X` (undefined,
      // not `| null`), so the typed overload rejects the null-clearing shape. Keep
      // the cast until the generator represents Optional-with-default as `| null`.
      const result = await rpcCall<EventRule>(client, 'events.UpdateRule', { customer_id: customerId, rule_id: ruleId, ...backendUpdates } as unknown as Record<string, unknown>);
      return toExtendedRule(result);
    },

    deleteEventRule: async (customerId: string, ruleId: string): Promise<void> => {
      await rpcCall(client, 'events.DeleteRule', { customer_id: customerId, rule_id: ruleId });
    },

    triggerRuleNow: async (
      customerId: string,
      ruleId: string,
      overwrite = false
    ): Promise<{ event_id?: string; triggered_at?: string; already_exists?: boolean; period_label?: string; period_key?: string }> => {
      return rpcCall(client, 'events.TriggerRuleNow', { customer_id: customerId, rule_id: ruleId, overwrite });
    },

    toggleRule: async (
      customerId: string,
      ruleId: string,
      enabled: boolean
    ): Promise<EventRuleExtended> => {
      const result = await rpcCall(client, 'events.UpdateRule', { customer_id: customerId, rule_id: ruleId, enabled });
      return toExtendedRule(result);
    },

    duplicateRule: async (
      customerId: string,
      ruleId: string,
      newName: string
    ): Promise<EventRuleExtended> => {
      const existing = await rpcCall(client, 'events.GetRule', { customer_id: customerId, rule_id: ruleId });

      const createRequest: RuleCreate = {
        customer_id: customerId,
        rule_type: existing.rule_type,
        name: newName,
        description: `Copy of ${existing.name}`,
        event_pattern: existing.event_pattern,
        enrichment_prompt_id: existing.enrichment_prompt_id,
        itsm: existing.itsm,
        notification_targets: existing.notification_targets,
        automation: existing.automation,
        enabled: false,
        event_examples: existing.event_examples,
        opsitem_strategy: existing.opsitem_strategy,
        dedup_field_paths: existing.dedup_field_paths ?? [],
      };

      const result = await rpcCall(client, 'events.CreateRule', { ...createRequest, customer_id: customerId });
      return toExtendedRule(result);
    },

    suggestFieldMappings: async (
      request: SuggestFieldMappingsRequest
    ): Promise<SuggestFieldMappingsResponse> => {
      return rpcCall(client, 'events.SuggestFieldMappings', request as unknown as Record<string, unknown>);
    },

    getRuleSampleEvent: async (request: GetRuleSampleEventRequest): Promise<RuleSampleEvent> => {
      return rpcCall(client, 'events.GetRuleSampleEvent', request as unknown as Record<string, unknown>);
    },

    previewRuleOutput: async (
      request: PreviewRuleOutputRequest
    ): Promise<PreviewRuleOutputResponse> => {
      return rpcCall(client, 'events.PreviewRuleOutput', request as unknown as Record<string, unknown>);
    },

    testEventPattern: async (
      customerId: string,
      request: PatternTestRequest
    ): Promise<PatternTestResponse> => {
      return rpcCall(client, 'events.TestPattern', { customer_id: customerId, ...request } as unknown as Record<string, unknown>);
    },

    listEventDatasets: async (customerId: string): Promise<EventDatasetRecord[]> => {
      return rpcCall(client, 'events.ListEventDatasets', { customer_id: customerId });
    },

    createEventDataset: async (dataset: EventDataset): Promise<EventDatasetRecord> => {
      return rpcCall(client, 'events.CreateEventDataset', dataset as unknown as Record<string, unknown>);
    },

    updateEventDataset: async (dataset: EventDataset): Promise<EventDatasetRecord> => {
      return rpcCall(client, 'events.UpdateEventDataset', dataset as unknown as Record<string, unknown>);
    },

    getEventDatasetStatus: async (customerId: string, name: string): Promise<EventDatasetStatus> => {
      return rpcCall(client, 'events.GetEventDatasetStatus', { customer_id: customerId, name });
    },

    getEventDatasetRetentionCeiling: async (customerId: string): Promise<EventDatasetRetentionCeiling> => {
      return rpcCall(client, 'events.GetEventDatasetRetentionCeiling', { customer_id: customerId });
    },

    listEventDatasetMisfits: async (
      customerId: string,
      name: string,
      column: string,
      kind: EventDatasetMisfit['kind'],
      limit: number,
    ): Promise<EventDatasetMisfit[]> => {
      return rpcCall(client, 'events.ListEventDatasetMisfits', { customer_id: customerId, name, column, kind, limit });
    },

    previewEventDataset: async (
      customerId: string,
      draft: EventDataset,
      sample: Record<string, unknown> | undefined,
    ): Promise<EventDatasetPreview> => {
      return rpcCall(client, 'events.PreviewEventDataset', {
        customer_id: customerId,
        draft: draft as unknown as Record<string, unknown>,
        ...(sample ? { sample } : {}),
      });
    },

    deleteEventDataset: async (customerId: string, name: string): Promise<EventDatasetDeleted> => {
      return rpcCall(client, 'events.DeleteEventDataset', { customer_id: customerId, name });
    },

    rebuildEventDataset: async (customerId: string, name: string): Promise<EventDatasetRecord> => {
      return rpcCall(client, 'events.RebuildEventDataset', { customer_id: customerId, name });
    },

    /** One read of a dataset's rows, or with count_only of how many rows match (EM-UC-37). */
    queryEventDataset: async (request: QueryEventDatasetRequest): Promise<EventDatasetRows> => {
      return rpcCall(client, 'events.QueryEventDataset', request);
    },

    /** The global dataset templates and this customer's own. */
    listEventDatasetTemplates: async (customerId: string): Promise<EventDatasetTemplate[]> => {
      return rpcCall(client, 'events.ListCustomerEventDatasetTemplates', { customer_id: customerId });
    },

    createCustomerEventDatasetTemplate: async (
      customerId: string,
      template: Omit<CreateCustomerEventDatasetTemplateRequest, 'customer_id'>,
    ): Promise<EventDatasetTemplate> => {
      return rpcCall(client, 'events.CreateCustomerEventDatasetTemplate', {
        customer_id: customerId,
        name: template.name,
        ...(template.description ? { description: template.description } : {}),
        definition: template.definition as unknown as Record<string, unknown>,
      });
    },

    deleteEventDatasetTemplate: async (customerId: string, templateId: string): Promise<EventDatasetTemplateDeleted> => {
      return rpcCall(client, 'events.DeleteEventDatasetTemplate', { customer_id: customerId, template_id: templateId });
    },

    testTransformation: async (
      customerId: string,
      request: TransformTestRequest
    ): Promise<TransformTestResponse> => {
      return rpcCall(client, 'events.TestTransform', { customer_id: customerId, ...request } as unknown as Record<string, unknown>);
    },

    getEvents: async (
      customerId: string,
      filters?: EventFilters
    ): Promise<EventsResponse> => {
      const rpcParams: Record<string, unknown> = { customer_id: customerId };
      if (filters?.startTime && filters?.endTime) {
        rpcParams.startTime = filters.startTime;
        rpcParams.endTime = filters.endTime;
      } else if (filters?.timeRange) {
        rpcParams.timeRange = filters.timeRange;
      }
      if (filters?.since) rpcParams.since = filters.since;
      if (filters?.source) rpcParams.source = filters.source;
      if (filters?.detailType) rpcParams.detailType = filters.detailType;
      if (filters?.region) rpcParams.region = filters.region;
      if (filters?.account) rpcParams.account = filters.account;
      if (filters?.limit) rpcParams.limit = filters.limit;
      if (filters?.excludeSources) rpcParams.excludeSources = filters.excludeSources;
      if (filters?.excludeDetailTypes) rpcParams.excludeDetailTypes = filters.excludeDetailTypes;
      if (filters?.excludeRegions) rpcParams.excludeRegions = filters.excludeRegions;
      if (filters?.excludeAccounts) rpcParams.excludeAccounts = filters.excludeAccounts;
      if (filters?.search) rpcParams.search = filters.search;
      return rpcCall(client, 'events.GetEvents', rpcParams);
    },

    getEventTrace: async (
      customerId: string,
      eventId: string,
      timestamp: string
    ): Promise<EventTrace> => {
      return rpcCall(client, 'events.GetEventTrace', { customer_id: customerId, event_id: eventId, timestamp });
    },

    getActionLog: async (
      customerId: string,
      params?: { rule_type?: string; status?: string; max_results?: number; next_token?: string; reports_only?: boolean }
    ): Promise<ActionLogResponse> => {
      const rpcParams: Record<string, unknown> = { customer_id: customerId };
      if (params?.rule_type) rpcParams.rule_type = params.rule_type;
      if (params?.status) rpcParams.status = params.status;
      if (params?.max_results) rpcParams.max_results = params.max_results;
      if (params?.next_token) rpcParams.next_token = params.next_token;
      if (params?.reports_only) rpcParams.reports_only = params.reports_only;
      return rpcCall(client, 'events.GetActionLog', rpcParams);
    },

    getActionLogItem: async (
      customerId: string,
      opsitemId: string
    ): Promise<ActionLogEntry> => {
      return rpcCall(client, 'events.GetActionLogItem', { customer_id: customerId, opsitem_id: opsitemId });
    },

    getReport: async (
      customerId: string,
      opsitemId: string
    ): Promise<{ opsitem_id: string; html: string }> => {
      return rpcCall(client, 'events.GetReport', { customer_id: customerId, opsitem_id: opsitemId });
    },

    getReportPdf: async (
      customerId: string,
      opsitemId: string
    ): Promise<{ opsitem_id: string; url: string; filename: string; expires_in: string }> => {
      return rpcCall(client, 'events.GetReportPdf', { customer_id: customerId, opsitem_id: opsitemId });
    },

    closeActionLogItem: async (
      customerId: string,
      opsitemId: string
    ): Promise<{ opsitem_id: string; status: string }> => {
      return rpcCall(client, 'events.CloseActionLogItem', { customer_id: customerId, opsitem_id: opsitemId });
    },

    deleteActionLogItem: async (
      customerId: string,
      opsitemId: string
    ): Promise<{ opsitem_id: string; deleted: boolean }> => {
      return rpcCall(client, 'events.DeleteActionLogItem', { customer_id: customerId, opsitem_id: opsitemId });
    },

    // Template operations

    getGlobalTemplates: async (): Promise<RuleTemplateListItem[]> => {
      return rpcCall<RuleTemplateListItem[]>(client, 'events.ListGlobalTemplates', {});
    },

    getTemplates: async (customerId: string): Promise<RuleTemplateListItem[]> => {
      const [customer, global] = await Promise.all([
        rpcCall<RuleTemplateListItem[]>(client, 'events.ListCustomerTemplates', { customer_id: customerId }),
        rpcCall<RuleTemplateListItem[]>(client, 'events.ListGlobalTemplates', { customer_id: customerId }),
      ]);
      return [...customer, ...global];
    },

    getTemplate: async (templateId: string, owner: TemplateOwnerType, customerId: string): Promise<RuleTemplate> => {
      return rpcCall<RuleTemplate>(client, 'events.GetTemplate', { template_id: templateId, owner, customer_id: customerId });
    },

    // TemplateCreate is extra='forbid' and a global template has no customer, so
    // CreateGlobalTemplate must not carry customer_id.
    createTemplate: async (
      data: Record<string, unknown>,
      owner: TemplateOwnerType,
      customerId: string,
    ): Promise<RuleTemplate> => {
      if (owner === 'customer') {
        return rpcCall(client, 'events.CreateCustomerTemplate', { ...data, customer_id: customerId });
      }
      return rpcCall(client, 'events.CreateGlobalTemplate', data);
    },

    // A global template is shared by every customer, so writing one is authorized
    // globally rather than against a single Customer — hence its own RPC method.
    updateTemplate: async (
      templateId: string,
      updates: Record<string, unknown>,
      owner: TemplateOwnerType,
      customerId: string,
    ): Promise<RuleTemplate> => {
      if (owner === 'customer') {
        return rpcCall<RuleTemplate>(client, 'events.UpdateTemplate', { template_id: templateId, customer_id: customerId, ...updates });
      }
      return rpcCall<RuleTemplate>(client, 'events.UpdateGlobalTemplate', { template_id: templateId, ...updates });
    },

    deleteTemplate: async (templateId: string, owner: TemplateOwnerType, customerId: string): Promise<void> => {
      if (owner === 'customer') {
        await rpcCall(client, 'events.DeleteTemplate', { template_id: templateId, customer_id: customerId });
        return;
      }
      await rpcCall(client, 'events.DeleteGlobalTemplate', { template_id: templateId });
    },

  };
};

/**
 * Hook to get event management service instance
 */
export const useEventManagementService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    return createEventManagementService(idToken);
  }, [idToken]);
};

/**
 * Hook for auto-refresh: periodically polls events.GetEvents for events newer
 * than the last seen, via REST. Not a stream — it re-fetches on an interval.
 *
 * @param customerId - Customer ID
 * @param onNewEvents - Callback for new events
 * @param pollIntervalMs - Polling interval in ms (default: 10000)
 */
export const useEventPolling = (
  customerId: string,
  onNewEvents: (events: LiveEvent[]) => void,
  pollIntervalMs: number = 10000,
  baseFilters?: EventFilters
) => {
  const { idToken } = useAuth();
  const lastTimestampRef = useRef<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const baseFiltersRef = useRef(baseFilters);
  baseFiltersRef.current = baseFilters;

  const startPolling = useCallback(() => {
    if (isPollingRef.current || !idToken) return;

    isPollingRef.current = true;
    const service = createEventManagementService(idToken);

    const poll = async () => {
      try {
        const currentBaseFilters = baseFiltersRef.current || {};
        const filters: EventFilters = lastTimestampRef.current
          ? { ...currentBaseFilters, since: lastTimestampRef.current, limit: 50 }
          : { ...currentBaseFilters, timeRange: currentBaseFilters.timeRange || '15m', limit: 100 };

        const response = await service.getEvents(customerId, filters);

        if (response.events.length > 0) {
          // Update last timestamp for next poll
          lastTimestampRef.current = response.events[0].timestamp;
          onNewEvents(response.events);
        }
      } catch (error) {
        console.error('Error polling events:', error);
      }
    };

    // Initial fetch
    poll();

    // Start interval
    intervalRef.current = setInterval(poll, pollIntervalMs);
  }, [customerId, idToken, onNewEvents, pollIntervalMs]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  return {
    startPolling,
    stopPolling,
  };
};

export const getScheduleExpression = (preset: string): string =>
  buildPresetCron(preset as SchedulePreset);

export const getPresetFromExpression = parsePreset;
